// ═══════════════════════════════════════════════════════════════════════════════
// ULTIMATE SERVER MONITORING SYSTEM - Frontend
// A comprehensive React dashboard with real-time metrics, charts, and alerts
// Minimalistic UI inspired by shadcn/ui
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { io } from 'socket.io-client';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import {
  Activity, Cpu, HardDrive, Network, Server, AlertTriangle, CheckCircle,
  XCircle, Clock, Zap, Database, Terminal, Settings, RefreshCw, Trash2,
  ChevronDown, ChevronUp, Monitor, Wifi, MemoryStick, Gauge, TrendingUp,
  TrendingDown, Bell, Eye, EyeOff, Download, Upload, Box, Layers, Play,
  Square, BarChart3, PieChart as PieChartIcon, List, Grid, Moon, Sun
} from 'lucide-react';

// ═══════════════════════════════════════════════════════════════════════════════
// CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════════

const API_URL = 'https://server-logs.donotes.app';
const SOCKET_URL = 'https://server-logs.donotes.app';

// ═══════════════════════════════════════════════════════════════════════════════
// THEME & STYLES
// ═══════════════════════════════════════════════════════════════════════════════

const theme = {
  dark: {
    bg: '#09090b',
    bgCard: '#18181b',
    bgHover: '#27272a',
    border: '#27272a',
    borderHover: '#3f3f46',
    text: '#fafafa',
    textMuted: '#a1a1aa',
    textDim: '#71717a',
    primary: '#3b82f6',
    primaryHover: '#2563eb',
    success: '#22c55e',
    warning: '#eab308',
    danger: '#ef4444',
    info: '#06b6d4',
    purple: '#a855f7',
    pink: '#ec4899',
  }
};

const colors = theme.dark;

// CSS-in-JS styles
const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: colors.bg,
    color: colors.text,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
  },
  header: {
    borderBottom: `1px solid ${colors.border}`,
    padding: '16px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'sticky',
    top: 0,
    backgroundColor: colors.bg,
    zIndex: 50,
    backdropFilter: 'blur(8px)',
  },
  main: {
    padding: '24px',
    maxWidth: '1800px',
    margin: '0 auto',
  },
  card: {
    backgroundColor: colors.bgCard,
    borderRadius: '8px',
    border: `1px solid ${colors.border}`,
    overflow: 'hidden',
  },
  cardHeader: {
    padding: '16px',
    borderBottom: `1px solid ${colors.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  cardContent: {
    padding: '16px',
  },
  grid: {
    display: 'grid',
    gap: '16px',
  },
  button: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '8px 16px',
    borderRadius: '6px',
    border: `1px solid ${colors.border}`,
    backgroundColor: 'transparent',
    color: colors.text,
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    transition: 'all 0.15s ease',
  },
  buttonPrimary: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 8px',
    borderRadius: '9999px',
    fontSize: '12px',
    fontWeight: 500,
  },
  input: {
    width: '100%',
    padding: '8px 12px',
    borderRadius: '6px',
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.bg,
    color: colors.text,
    fontSize: '14px',
    outline: 'none',
  },
  tabs: {
    display: 'flex',
    gap: '4px',
    padding: '4px',
    backgroundColor: colors.bg,
    borderRadius: '8px',
  },
  tab: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: 'transparent',
    color: colors.textMuted,
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    transition: 'all 0.15s ease',
  },
  tabActive: {
    backgroundColor: colors.bgCard,
    color: colors.text,
  },
  statCard: {
    padding: '20px',
    borderRadius: '8px',
    backgroundColor: colors.bgCard,
    border: `1px solid ${colors.border}`,
  },
  progressBar: {
    width: '100%',
    height: '8px',
    backgroundColor: colors.bg,
    borderRadius: '4px',
    overflow: 'hidden',
  },
  tooltip: {
    backgroundColor: colors.bgCard,
    border: `1px solid ${colors.border}`,
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
// UTILITY FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════════

const formatBytes = (bytes, decimals = 1) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(decimals)) + ' ' + sizes[i];
};

const formatTime = (timestamp) => {
  return new Date(timestamp).toLocaleTimeString();
};

const formatDuration = (seconds) => {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

const getStatusColor = (value, thresholds = { warning: 70, critical: 90 }) => {
  if (value >= thresholds.critical) return colors.danger;
  if (value >= thresholds.warning) return colors.warning;
  return colors.success;
};

// ═══════════════════════════════════════════════════════════════════════════════
// COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

// Card Component
const Card = ({ children, style = {}, className = '' }) => (
  <div style={{ ...styles.card, ...style }} className={className}>
    {children}
  </div>
);

// Stat Card Component
const StatCard = ({ icon: Icon, title, value, subValue, trend, color = colors.primary, progress }) => (
  <div style={styles.statCard}>
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '12px' }}>
      <div style={{ 
        padding: '8px', 
        borderRadius: '8px', 
        backgroundColor: `${color}20`,
        color: color 
      }}>
        <Icon size={20} />
      </div>
      {trend !== undefined && (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '4px',
          color: trend >= 0 ? colors.success : colors.danger,
          fontSize: '12px'
        }}>
          {trend >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
          {Math.abs(trend).toFixed(1)}%
        </div>
      )}
    </div>
    <div style={{ fontSize: '24px', fontWeight: 600, marginBottom: '4px' }}>{value}</div>
    <div style={{ fontSize: '13px', color: colors.textMuted }}>{title}</div>
    {subValue && <div style={{ fontSize: '12px', color: colors.textDim, marginTop: '4px' }}>{subValue}</div>}
    {progress !== undefined && (
      <div style={{ marginTop: '12px' }}>
        <div style={styles.progressBar}>
          <div style={{
            width: `${Math.min(100, progress)}%`,
            height: '100%',
            backgroundColor: getStatusColor(progress),
            transition: 'width 0.3s ease, background-color 0.3s ease',
          }} />
        </div>
      </div>
    )}
  </div>
);

// Badge Component
const Badge = ({ children, variant = 'default' }) => {
  const variantStyles = {
    default: { backgroundColor: colors.bgHover, color: colors.text },
    success: { backgroundColor: `${colors.success}20`, color: colors.success },
    warning: { backgroundColor: `${colors.warning}20`, color: colors.warning },
    danger: { backgroundColor: `${colors.danger}20`, color: colors.danger },
    info: { backgroundColor: `${colors.info}20`, color: colors.info },
  };
  return <span style={{ ...styles.badge, ...variantStyles[variant] }}>{children}</span>;
};

// Button Component
const Button = ({ children, onClick, variant = 'default', size = 'default', disabled = false, style = {} }) => {
  const sizeStyles = {
    sm: { padding: '4px 8px', fontSize: '12px' },
    default: { padding: '8px 16px', fontSize: '14px' },
    lg: { padding: '12px 24px', fontSize: '16px' },
  };
  const variantStyles = {
    default: {},
    primary: styles.buttonPrimary,
    danger: { backgroundColor: colors.danger, borderColor: colors.danger },
    ghost: { border: 'none', backgroundColor: 'transparent' },
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        ...styles.button,
        ...sizeStyles[size],
        ...variantStyles[variant],
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        ...style,
      }}
      onMouseEnter={(e) => {
        if (!disabled) e.target.style.backgroundColor = colors.bgHover;
      }}
      onMouseLeave={(e) => {
        if (!disabled) e.target.style.backgroundColor = variantStyles[variant].backgroundColor || 'transparent';
      }}
    >
      {children}
    </button>
  );
};

// Tab Component
const Tabs = ({ tabs, activeTab, onChange }) => (
  <div style={styles.tabs}>
    {tabs.map((tab) => (
      <button
        key={tab.id}
        onClick={() => onChange(tab.id)}
        style={{
          ...styles.tab,
          ...(activeTab === tab.id ? styles.tabActive : {}),
        }}
      >
        {tab.icon && <tab.icon size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />}
        {tab.label}
      </button>
    ))}
  </div>
);

// Circular Progress Component
const CircularProgress = ({ value, size = 120, strokeWidth = 8, color, label, subLabel }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (value / 100) * circumference;
  const progressColor = color || getStatusColor(value);

  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={colors.bg}
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={progressColor}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.5s ease, stroke 0.3s ease' }}
        />
      </svg>
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: '24px', fontWeight: 600 }}>{value.toFixed(0)}%</div>
        {label && <div style={{ fontSize: '12px', color: colors.textMuted }}>{label}</div>}
        {subLabel && <div style={{ fontSize: '11px', color: colors.textDim }}>{subLabel}</div>}
      </div>
    </div>
  );
};

// Sparkline Component
const Sparkline = ({ data, color = colors.primary, height = 40, width = 120 }) => {
  if (!data || data.length < 2) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((v - min) / range) * height;
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg width={width} height={height}>
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

// Custom Tooltip for Charts
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div style={styles.tooltip}>
      <div style={{ fontSize: '12px', color: colors.textMuted, marginBottom: '4px' }}>
        {typeof label === 'number' ? formatTime(label) : label}
      </div>
      {payload.map((p, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
          <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: p.color }} />
          <span style={{ color: colors.textMuted }}>{p.name}:</span>
          <span style={{ fontWeight: 500 }}>{typeof p.value === 'number' ? p.value.toFixed(1) : p.value}</span>
        </div>
      ))}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// DASHBOARD SECTIONS
// ═══════════════════════════════════════════════════════════════════════════════

// Overview Stats Section
const OverviewStats = ({ metrics, history }) => {
  const cpuTrend = useMemo(() => {
    if (!history?.cpu || history.cpu.length < 10) return 0;
    const recent = history.cpu.slice(-10);
    const older = history.cpu.slice(-20, -10);
    const recentAvg = recent.reduce((a, b) => a + b, 0) / recent.length;
    const olderAvg = older.reduce((a, b) => a + b, 0) / older.length || recentAvg;
    return ((recentAvg - olderAvg) / olderAvg) * 100;
  }, [history?.cpu]);

  return (
    <div style={{ ...styles.grid, gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
      <StatCard
        icon={Cpu}
        title="CPU Usage"
        value={`${metrics?.cpu?.toFixed(1) || 0}%`}
        subValue={`${metrics?.cpuDetails?.cores || 0} cores @ ${metrics?.cpuDetails?.speed || 0} MHz`}
        color={getStatusColor(metrics?.cpu || 0)}
        progress={metrics?.cpu}
        trend={cpuTrend}
      />
      <StatCard
        icon={MemoryStick}
        title="Memory Usage"
        value={`${metrics?.memory?.usedPercent?.toFixed(1) || 0}%`}
        subValue={`${metrics?.memory?.formatted?.used || '0'} / ${metrics?.memory?.formatted?.total || '0'}`}
        color={getStatusColor(metrics?.memory?.usedPercent || 0)}
        progress={metrics?.memory?.usedPercent}
      />
      <StatCard
        icon={HardDrive}
        title="Disk Usage"
        value={`${metrics?.disk?.[0]?.usedPercent || 0}%`}
        subValue={`${metrics?.disk?.[0]?.formatted?.used || '0'} / ${metrics?.disk?.[0]?.formatted?.total || '0'}`}
        color={getStatusColor(metrics?.disk?.[0]?.usedPercent || 0)}
        progress={metrics?.disk?.[0]?.usedPercent}
      />
      <StatCard
        icon={Gauge}
        title="Load Average"
        value={metrics?.loadAvg?.['1m']?.toFixed(2) || '0.00'}
        subValue={`5m: ${metrics?.loadAvg?.['5m']?.toFixed(2) || '0'} | 15m: ${metrics?.loadAvg?.['15m']?.toFixed(2) || '0'}`}
        color={colors.info}
      />
      <StatCard
        icon={Clock}
        title="System Uptime"
        value={formatDuration(metrics?.system?.uptime || 0)}
        subValue={`Server: ${formatDuration(metrics?.system?.serverUptime || 0)}`}
        color={colors.purple}
      />
      <StatCard
        icon={Network}
        title="Network I/O"
        value={metrics?.network?.speed?.rxFormatted || '0 B/s'}
        subValue={`↑ ${metrics?.network?.speed?.txFormatted || '0 B/s'}`}
        color={colors.info}
      />
      <StatCard
        icon={Activity}
        title="Active Connections"
        value={metrics?.system?.activeConnections || 0}
        subValue={`Total requests: ${metrics?.system?.totalRequests || 0}`}
        color={colors.pink}
      />
      <StatCard
        icon={AlertTriangle}
        title="Active Alerts"
        value={metrics?.recentAlerts?.length || 0}
        subValue={`Last hour`}
        color={metrics?.recentAlerts?.length > 0 ? colors.warning : colors.success}
      />
    </div>
  );
};

// CPU Details Section
const CPUSection = ({ metrics, history }) => {
  const chartData = useMemo(() => {
    if (!history?.timestamps) return [];
    return history.timestamps.slice(-60).map((t, i) => ({
      time: t,
      cpu: history.cpu[history.cpu.length - 60 + i] || 0,
      load: history.loadAvg[history.loadAvg.length - 60 + i] || 0,
    }));
  }, [history]);

  return (
    <div style={{ ...styles.grid, gridTemplateColumns: '1fr 300px' }}>
      <Card>
        <div style={styles.cardHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Cpu size={18} />
            <span style={{ fontWeight: 500 }}>CPU Usage Over Time</span>
          </div>
        </div>
        <div style={{ ...styles.cardContent, height: '300px' }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="cpuGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={colors.primary} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={colors.primary} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={colors.border} vertical={false} />
              <XAxis 
                dataKey="time" 
                tickFormatter={formatTime} 
                stroke={colors.textDim}
                tick={{ fontSize: 11 }}
                axisLine={{ stroke: colors.border }}
              />
              <YAxis 
                domain={[0, 100]} 
                stroke={colors.textDim}
                tick={{ fontSize: 11 }}
                axisLine={{ stroke: colors.border }}
                tickFormatter={(v) => `${v}%`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="cpu"
                name="CPU"
                stroke={colors.primary}
                fill="url(#cpuGradient)"
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>
      <Card>
        <div style={styles.cardHeader}>
          <span style={{ fontWeight: 500 }}>Per-Core Usage</span>
        </div>
        <div style={styles.cardContent}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {metrics?.cpuDetails?.perCore?.map((core) => (
              <div key={core.core}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span style={{ fontSize: '13px', color: colors.textMuted }}>Core {core.core}</span>
                  <span style={{ fontSize: '13px', fontWeight: 500 }}>{core.usage}%</span>
                </div>
                <div style={styles.progressBar}>
                  <div style={{
                    width: `${core.usage}%`,
                    height: '100%',
                    backgroundColor: getStatusColor(core.usage),
                    transition: 'all 0.3s ease',
                  }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
};

// Memory Details Section
const MemorySection = ({ metrics, history }) => {
  const chartData = useMemo(() => {
    if (!history?.timestamps) return [];
    return history.timestamps.slice(-60).map((t, i) => ({
      time: t,
      memory: history.memory[history.memory.length - 60 + i] || 0,
    }));
  }, [history]);

  const pieData = [
    { name: 'Used', value: metrics?.memory?.used || 0, color: colors.primary },
    { name: 'Cached', value: metrics?.memory?.cached || 0, color: colors.info },
    { name: 'Buffers', value: metrics?.memory?.buffers || 0, color: colors.purple },
    { name: 'Free', value: metrics?.memory?.free || 0, color: colors.bgHover },
  ];

  return (
    <div style={{ ...styles.grid, gridTemplateColumns: '1fr 300px' }}>
      <Card>
        <div style={styles.cardHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <MemoryStick size={18} />
            <span style={{ fontWeight: 500 }}>Memory Usage Over Time</span>
          </div>
        </div>
        <div style={{ ...styles.cardContent, height: '300px' }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="memGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={colors.success} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={colors.success} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={colors.border} vertical={false} />
              <XAxis dataKey="time" tickFormatter={formatTime} stroke={colors.textDim} tick={{ fontSize: 11 }} />
              <YAxis domain={[0, 100]} stroke={colors.textDim} tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="memory" name="Memory" stroke={colors.success} fill="url(#memGradient)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>
      <Card>
        <div style={styles.cardHeader}>
          <span style={{ fontWeight: 500 }}>Memory Breakdown</span>
        </div>
        <div style={{ ...styles.cardContent, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={70}
                paddingAngle={2}
                dataKey="value"
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => formatBytes(value)} />
            </PieChart>
          </ResponsiveContainer>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'center', marginTop: '8px' }}>
            {pieData.map((item) => (
              <div key={item.name} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                <div style={{ width: '10px', height: '10px', borderRadius: '2px', backgroundColor: item.color }} />
                <span style={{ color: colors.textMuted }}>{item.name}</span>
              </div>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
};

// Network Section
const NetworkSection = ({ metrics, history }) => {
  const chartData = useMemo(() => {
    if (!history?.timestamps) return [];
    return history.timestamps.slice(-60).map((t, i) => {
      const net = history.network[history.network.length - 60 + i] || { rx: 0, tx: 0 };
      return {
        time: t,
        download: Math.max(0, net.rx) / 1024,
        upload: Math.max(0, net.tx) / 1024,
      };
    });
  }, [history]);

  return (
    <Card>
      <div style={styles.cardHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Network size={18} />
          <span style={{ fontWeight: 500 }}>Network Traffic</span>
        </div>
        <div style={{ display: 'flex', gap: '16px', fontSize: '13px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Download size={14} style={{ color: colors.info }} />
            <span style={{ color: colors.textMuted }}>RX:</span>
            <span style={{ fontWeight: 500 }}>{metrics?.network?.speed?.rxFormatted || '0 B/s'}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Upload size={14} style={{ color: colors.purple }} />
            <span style={{ color: colors.textMuted }}>TX:</span>
            <span style={{ fontWeight: 500 }}>{metrics?.network?.speed?.txFormatted || '0 B/s'}</span>
          </div>
        </div>
      </div>
      <div style={{ ...styles.cardContent, height: '250px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="downloadGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={colors.info} stopOpacity={0.3} />
                <stop offset="95%" stopColor={colors.info} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="uploadGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={colors.purple} stopOpacity={0.3} />
                <stop offset="95%" stopColor={colors.purple} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={colors.border} vertical={false} />
            <XAxis dataKey="time" tickFormatter={formatTime} stroke={colors.textDim} tick={{ fontSize: 11 }} />
            <YAxis stroke={colors.textDim} tick={{ fontSize: 11 }} tickFormatter={(v) => `${v.toFixed(0)} KB`} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Area type="monotone" dataKey="download" name="Download (KB/s)" stroke={colors.info} fill="url(#downloadGrad)" strokeWidth={2} />
            <Area type="monotone" dataKey="upload" name="Upload (KB/s)" stroke={colors.purple} fill="url(#uploadGrad)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
};

// Disk Section
const DiskSection = ({ metrics }) => {
  return (
    <Card>
      <div style={styles.cardHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <HardDrive size={18} />
          <span style={{ fontWeight: 500 }}>Disk Usage</span>
        </div>
      </div>
      <div style={styles.cardContent}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {metrics?.disk?.map((disk, i) => (
            <div key={i} style={{ 
              padding: '12px', 
              backgroundColor: colors.bg, 
              borderRadius: '6px',
              border: `1px solid ${colors.border}`
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <div>
                  <div style={{ fontWeight: 500, marginBottom: '2px' }}>{disk.mountpoint}</div>
                  <div style={{ fontSize: '12px', color: colors.textDim }}>{disk.filesystem}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 600, color: getStatusColor(disk.usedPercent) }}>{disk.usedPercent}%</div>
                  <div style={{ fontSize: '12px', color: colors.textDim }}>
                    {disk.formatted.used} / {disk.formatted.total}
                  </div>
                </div>
              </div>
              <div style={styles.progressBar}>
                <div style={{
                  width: `${disk.usedPercent}%`,
                  height: '100%',
                  backgroundColor: getStatusColor(disk.usedPercent),
                  transition: 'all 0.3s ease',
                }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
};

// Process List Section
const ProcessSection = ({ metrics }) => {
  const [sortBy, setSortBy] = useState('cpu');
  const [sortDir, setSortDir] = useState('desc');
  const [filter, setFilter] = useState('');

  const sortedProcesses = useMemo(() => {
    let procs = metrics?.processes || [];
    if (filter) {
      procs = procs.filter(p => 
        p.command.toLowerCase().includes(filter.toLowerCase()) ||
        p.user.toLowerCase().includes(filter.toLowerCase())
      );
    }
    return [...procs].sort((a, b) => {
      const aVal = sortBy === 'cpu' ? a.cpu : a.memory;
      const bVal = sortBy === 'cpu' ? b.cpu : b.memory;
      return sortDir === 'desc' ? bVal - aVal : aVal - bVal;
    });
  }, [metrics?.processes, sortBy, sortDir, filter]);

  return (
    <Card>
      <div style={styles.cardHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Terminal size={18} />
          <span style={{ fontWeight: 500 }}>Processes</span>
          <Badge>{sortedProcesses.length}</Badge>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <input
            type="text"
            placeholder="Filter processes..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{ ...styles.input, width: '200px' }}
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (sortBy === 'cpu') {
                setSortDir(sortDir === 'desc' ? 'asc' : 'desc');
              } else {
                setSortBy('cpu');
                setSortDir('desc');
              }
            }}
          >
            <Cpu size={14} /> CPU
            {sortBy === 'cpu' && (sortDir === 'desc' ? <ChevronDown size={14} /> : <ChevronUp size={14} />)}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (sortBy === 'memory') {
                setSortDir(sortDir === 'desc' ? 'asc' : 'desc');
              } else {
                setSortBy('memory');
                setSortDir('desc');
              }
            }}
          >
            <MemoryStick size={14} /> Mem
            {sortBy === 'memory' && (sortDir === 'desc' ? <ChevronDown size={14} /> : <ChevronUp size={14} />)}
          </Button>
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
              <th style={{ padding: '12px 16px', textAlign: 'left', color: colors.textMuted, fontWeight: 500 }}>PID</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', color: colors.textMuted, fontWeight: 500 }}>User</th>
              <th style={{ padding: '12px 16px', textAlign: 'right', color: colors.textMuted, fontWeight: 500 }}>CPU %</th>
              <th style={{ padding: '12px 16px', textAlign: 'right', color: colors.textMuted, fontWeight: 500 }}>Mem %</th>
              <th style={{ padding: '12px 16px', textAlign: 'right', color: colors.textMuted, fontWeight: 500 }}>RSS</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', color: colors.textMuted, fontWeight: 500 }}>Command</th>
            </tr>
          </thead>
          <tbody>
            {sortedProcesses.slice(0, 20).map((proc) => (
              <tr 
                key={proc.pid} 
                style={{ 
                  borderBottom: `1px solid ${colors.border}`,
                  transition: 'background-color 0.15s ease',
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.bgHover}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <td style={{ padding: '10px 16px', fontFamily: 'monospace' }}>{proc.pid}</td>
                <td style={{ padding: '10px 16px', color: colors.textMuted }}>{proc.user}</td>
                <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                  <span style={{ 
                    color: getStatusColor(proc.cpu, { warning: 30, critical: 60 }),
                    fontWeight: proc.cpu > 10 ? 600 : 400
                  }}>
                    {proc.cpu.toFixed(1)}
                  </span>
                </td>
                <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                  <span style={{ 
                    color: getStatusColor(proc.memory, { warning: 10, critical: 30 }),
                    fontWeight: proc.memory > 5 ? 600 : 400
                  }}>
                    {proc.memory.toFixed(1)}
                  </span>
                </td>
                <td style={{ padding: '10px 16px', textAlign: 'right', color: colors.textMuted }}>
                  {formatBytes(proc.rss)}
                </td>
                <td style={{ 
                  padding: '10px 16px', 
                  maxWidth: '400px', 
                  overflow: 'hidden', 
                  textOverflow: 'ellipsis', 
                  whiteSpace: 'nowrap',
                  fontFamily: 'monospace',
                  fontSize: '12px'
                }}>
                  {proc.command}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

// Alerts Section
const AlertsSection = ({ alerts, onClearAlerts }) => {
  const [severityFilter, setSeverityFilter] = useState('all');

  const filteredAlerts = useMemo(() => {
    if (severityFilter === 'all') return alerts;
    return alerts.filter(a => a.severity === severityFilter);
  }, [alerts, severityFilter]);

  return (
    <Card>
      <div style={styles.cardHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <AlertTriangle size={18} />
          <span style={{ fontWeight: 500 }}>Alerts & Logs</span>
          <Badge variant={alerts.length > 0 ? 'warning' : 'success'}>{alerts.length}</Badge>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Tabs
            tabs={[
              { id: 'all', label: 'All' },
              { id: 'critical', label: 'Critical' },
              { id: 'warning', label: 'Warning' },
            ]}
            activeTab={severityFilter}
            onChange={setSeverityFilter}
          />
          <Button variant="ghost" size="sm" onClick={onClearAlerts}>
            <Trash2 size={14} /> Clear
          </Button>
        </div>
      </div>
      <div style={{ ...styles.cardContent, maxHeight: '400px', overflowY: 'auto' }}>
        {filteredAlerts.length === 0 ? (
          <div style={{ 
            padding: '40px', 
            textAlign: 'center', 
            color: colors.textMuted 
          }}>
            <CheckCircle size={48} style={{ marginBottom: '12px', opacity: 0.5 }} />
            <div>No alerts to display</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {filteredAlerts.map((alert) => (
              <div
                key={alert.id}
                style={{
                  padding: '12px',
                  borderRadius: '6px',
                  backgroundColor: colors.bg,
                  border: `1px solid ${alert.severity === 'critical' ? colors.danger : colors.warning}30`,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                }}
              >
                {alert.severity === 'critical' ? (
                  <XCircle size={18} style={{ color: colors.danger, flexShrink: 0, marginTop: '2px' }} />
                ) : (
                  <AlertTriangle size={18} style={{ color: colors.warning, flexShrink: 0, marginTop: '2px' }} />
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span style={{ fontWeight: 500 }}>{alert.message}</span>
                    <Badge variant={alert.severity === 'critical' ? 'danger' : 'warning'}>
                      {alert.severity}
                    </Badge>
                  </div>
                  <div style={{ fontSize: '12px', color: colors.textDim }}>
                    {new Date(alert.timestamp).toLocaleString()} • 
                    Value: {alert.value.toFixed(1)} • 
                    Threshold: {alert.threshold}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};

// System Info Section
const SystemInfoSection = ({ metrics }) => {
  return (
    <div style={{ ...styles.grid, gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
      <Card>
        <div style={styles.cardHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Server size={18} />
            <span style={{ fontWeight: 500 }}>System Information</span>
          </div>
        </div>
        <div style={styles.cardContent}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {[
              { label: 'Hostname', value: metrics?.system?.hostname },
              { label: 'Platform', value: `${metrics?.system?.platform} (${metrics?.system?.arch})` },
              { label: 'Kernel', value: metrics?.system?.release },
              { label: 'Node.js', value: metrics?.system?.nodeVersion },
              { label: 'CPU Model', value: metrics?.cpuDetails?.model },
              { label: 'CPU Cores', value: metrics?.cpuDetails?.cores },
              { label: 'Total Memory', value: metrics?.memory?.formatted?.total },
              { label: 'Timezone', value: metrics?.system?.env?.TZ },
            ].map((item) => (
              <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: colors.textMuted, fontSize: '13px' }}>{item.label}</span>
                <span style={{ fontSize: '13px', fontFamily: 'monospace' }}>{item.value || '-'}</span>
              </div>
            ))}
          </div>
        </div>
      </Card>
      <Card>
        <div style={styles.cardHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Activity size={18} />
            <span style={{ fontWeight: 500 }}>Quick Stats</span>
          </div>
        </div>
        <div style={styles.cardContent}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div style={{ textAlign: 'center', padding: '16px', backgroundColor: colors.bg, borderRadius: '8px' }}>
              <CircularProgress 
                value={metrics?.cpu || 0} 
                size={80} 
                strokeWidth={6}
                label="CPU"
              />
            </div>
            <div style={{ textAlign: 'center', padding: '16px', backgroundColor: colors.bg, borderRadius: '8px' }}>
              <CircularProgress 
                value={metrics?.memory?.usedPercent || 0} 
                size={80} 
                strokeWidth={6}
                label="Memory"
              />
            </div>
            <div style={{ textAlign: 'center', padding: '16px', backgroundColor: colors.bg, borderRadius: '8px' }}>
              <CircularProgress 
                value={metrics?.disk?.[0]?.usedPercent || 0} 
                size={80} 
                strokeWidth={6}
                label="Disk"
              />
            </div>
            <div style={{ textAlign: 'center', padding: '16px', backgroundColor: colors.bg, borderRadius: '8px' }}>
              <div style={{ fontSize: '24px', fontWeight: 600 }}>{metrics?.processes?.length || 0}</div>
              <div style={{ fontSize: '12px', color: colors.textMuted, marginTop: '8px' }}>Processes</div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

// Settings Section
const SettingsSection = ({ config, onUpdateConfig }) => {
  const [thresholds, setThresholds] = useState(config?.thresholds || {
    cpu: 80,
    memory: 85,
    disk: 90,
    loadAvg: 2.0
  });

  const handleSave = async () => {
    try {
      const response = await fetch(`${API_URL}/api/config/thresholds`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(thresholds)
      });
      const data = await response.json();
      if (data.success) {
        onUpdateConfig(data.thresholds);
      }
    } catch (e) {
      console.error('Failed to update config:', e);
    }
  };

  return (
    <Card>
      <div style={styles.cardHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Settings size={18} />
          <span style={{ fontWeight: 500 }}>Alert Thresholds</span>
        </div>
      </div>
      <div style={styles.cardContent}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
          {[
            { key: 'cpu', label: 'CPU Usage (%)', icon: Cpu },
            { key: 'memory', label: 'Memory Usage (%)', icon: MemoryStick },
            { key: 'disk', label: 'Disk Usage (%)', icon: HardDrive },
            { key: 'loadAvg', label: 'Load Avg Multiplier', icon: Gauge },
          ].map(({ key, label, icon: Icon }) => (
            <div key={key}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px', 
                marginBottom: '8px',
                fontSize: '13px',
                color: colors.textMuted
              }}>
                <Icon size={14} />
                {label}
              </label>
              <input
                type="number"
                value={thresholds[key]}
                onChange={(e) => setThresholds({ ...thresholds, [key]: parseFloat(e.target.value) })}
                style={styles.input}
                min={0}
                max={key === 'loadAvg' ? 10 : 100}
                step={key === 'loadAvg' ? 0.1 : 1}
              />
            </div>
          ))}
        </div>
        <div style={{ marginTop: '24px' }}>
          <Button variant="primary" onClick={handleSave}>
            Save Thresholds
          </Button>
        </div>
      </div>
    </Card>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN APP COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

export default function App() {
  const [metrics, setMetrics] = useState(null);
  const [history, setHistory] = useState(null);
  const [config, setConfig] = useState(null);
  const [connected, setConnected] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const socketRef = useRef(null);

  // Initialize socket connection
  useEffect(() => {
    socketRef.current = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
    });

    socketRef.current.on('connect', () => {
      setConnected(true);
      console.log('Connected to server');
    });

    socketRef.current.on('disconnect', () => {
      setConnected(false);
      console.log('Disconnected from server');
    });

    socketRef.current.on('metrics', (data) => {
      if (autoRefresh) {
        setMetrics(data);
      }
    });

    socketRef.current.on('history', (data) => {
      setHistory(data);
    });

    // Request history on connect
    socketRef.current.on('connect', () => {
      socketRef.current.emit('requestHistory', 300);
    });

    return () => {
      socketRef.current?.disconnect();
    };
  }, [autoRefresh]);

  // Fetch history periodically
  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const response = await fetch(`${API_URL}/api/history?duration=300`);
        const data = await response.json();
        setHistory(data);
      } catch (e) {
        console.error('Failed to fetch history:', e);
      }
    };

    fetchHistory();
    const interval = setInterval(fetchHistory, 5000);
    return () => clearInterval(interval);
  }, []);

  // Fetch config
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch(`${API_URL}/api/config`);
        const data = await response.json();
        setConfig(data);
      } catch (e) {
        console.error('Failed to fetch config:', e);
      }
    };
    fetchConfig();
  }, []);

  const clearAlerts = async () => {
    try {
      await fetch(`${API_URL}/api/alerts`, { method: 'DELETE' });
      setMetrics(prev => prev ? { ...prev, recentAlerts: [] } : prev);
    } catch (e) {
      console.error('Failed to clear alerts:', e);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Grid },
    { id: 'cpu', label: 'CPU', icon: Cpu },
    { id: 'memory', label: 'Memory', icon: MemoryStick },
    { id: 'network', label: 'Network', icon: Network },
    { id: 'disk', label: 'Disk', icon: HardDrive },
    { id: 'processes', label: 'Processes', icon: Terminal },
    { id: 'alerts', label: 'Alerts', icon: Bell },
    { id: 'system', label: 'System', icon: Server },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Monitor size={24} style={{ color: colors.primary }} />
            <span style={{ fontSize: '18px', fontWeight: 600 }}>Server Monitor</span>
          </div>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '6px',
            padding: '4px 10px',
            borderRadius: '9999px',
            backgroundColor: connected ? `${colors.success}20` : `${colors.danger}20`,
            color: connected ? colors.success : colors.danger,
            fontSize: '12px',
            fontWeight: 500,
          }}>
            <div style={{ 
              width: '6px', 
              height: '6px', 
              borderRadius: '50%', 
              backgroundColor: 'currentColor',
              animation: connected ? 'pulse 2s infinite' : 'none'
            }} />
            {connected ? 'Connected' : 'Disconnected'}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
          <Button
            variant={autoRefresh ? 'primary' : 'default'}
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            <RefreshCw size={14} style={{ animation: autoRefresh ? 'spin 2s linear infinite' : 'none' }} />
            {autoRefresh ? 'Live' : 'Paused'}
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main style={styles.main}>
        {!metrics ? (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '400px',
            color: colors.textMuted
          }}>
            <div style={{ textAlign: 'center' }}>
              <RefreshCw size={48} style={{ marginBottom: '16px', animation: 'spin 2s linear infinite' }} />
              <div>Loading metrics...</div>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {activeTab === 'overview' && (
              <>
                <OverviewStats metrics={metrics} history={history} />
                <div style={{ ...styles.grid, gridTemplateColumns: '2fr 1fr' }}>
                  <CPUSection metrics={metrics} history={history} />
                </div>
                <NetworkSection metrics={metrics} history={history} />
                <div style={{ ...styles.grid, gridTemplateColumns: '1fr 1fr' }}>
                  <DiskSection metrics={metrics} />
                  <AlertsSection alerts={metrics.recentAlerts || []} onClearAlerts={clearAlerts} />
                </div>
              </>
            )}
            {activeTab === 'cpu' && <CPUSection metrics={metrics} history={history} />}
            {activeTab === 'memory' && <MemorySection metrics={metrics} history={history} />}
            {activeTab === 'network' && <NetworkSection metrics={metrics} history={history} />}
            {activeTab === 'disk' && <DiskSection metrics={metrics} />}
            {activeTab === 'processes' && <ProcessSection metrics={metrics} />}
            {activeTab === 'alerts' && <AlertsSection alerts={metrics.recentAlerts || []} onClearAlerts={clearAlerts} />}
            {activeTab === 'system' && <SystemInfoSection metrics={metrics} />}
            {activeTab === 'settings' && (
              <SettingsSection 
                config={config} 
                onUpdateConfig={(thresholds) => setConfig(prev => ({ ...prev, thresholds }))} 
              />
            )}
          </div>
        )}
      </main>

      {/* Global Styles */}
      <style>{`
        * {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }
        
        ::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        
        ::-webkit-scrollbar-track {
          background: ${colors.bg};
        }
        
        ::-webkit-scrollbar-thumb {
          background: ${colors.border};
          border-radius: 4px;
        }
        
        ::-webkit-scrollbar-thumb:hover {
          background: ${colors.borderHover};
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        
        input:focus {
          border-color: ${colors.primary};
          box-shadow: 0 0 0 2px ${colors.primary}30;
        }
        
        button:focus {
          outline: none;
          box-shadow: 0 0 0 2px ${colors.primary}30;
        }
      `}</style>
    </div>
  );
}
